import React, { useState, useEffect } from "react";
import { Input } from "../ui/input";
import { Alert, AlertDescription } from "../ui/alert";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { SubCategoryService } from "../../services/subCategoryService";
import { useDebounce } from "../../hooks/useDebounce";

const PrefixValidationInput = ({
  value,
  onChange,
  organizationId,
  getAccessTokenSilently,
  disabled,
  placeholder = "e.g., NETSEC, DATAPR",
  maxLength = 10,
  className = "",
}) => {
  const [validationState, setValidationState] = useState(null); // null, 'valid', 'invalid', 'checking'
  const [validationMessage, setValidationMessage] = useState("");

  const debouncedValue = useDebounce(value, 500);

  useEffect(() => {
    if (!debouncedValue || debouncedValue.length < 1) {
      setValidationState(null);
      setValidationMessage("");
      return;
    }

    validatePrefix(debouncedValue);
  }, [debouncedValue, organizationId]);

  const validatePrefix = async (prefix) => {
    if (!organizationId || !getAccessTokenSilently) {
      return;
    }

    try {
      setValidationState("checking");

      const response = await SubCategoryService.validatePrefix(
        prefix,
        organizationId,
        getAccessTokenSilently
      );

      if (response.is_valid) {
        setValidationState("valid");
        setValidationMessage(response.message);
      } else {
        setValidationState("invalid");
        setValidationMessage(response.message);
      }
    } catch (err) {
      setValidationState("invalid");
      setValidationMessage(err.message);
    }
  };

  const getValidationIcon = () => {
    switch (validationState) {
      case "checking":
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      case "valid":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "invalid":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getBorderColor = () => {
    switch (validationState) {
      case "valid":
        return "border-green-500 focus:border-green-500";
      case "invalid":
        return "border-red-500 focus:border-red-500";
      default:
        return "";
    }
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          className={`pr-10 ${getBorderColor()} ${className}`}
        />
        <div className="absolute right-3 top-3">{getValidationIcon()}</div>
      </div>

      {validationMessage && (
        <Alert
          className={`text-xs ${
            validationState === "valid"
              ? "border-green-200 bg-green-50"
              : validationState === "invalid"
              ? "border-red-200 bg-red-50"
              : ""
          }`}
        >
          <AlertDescription
            className={
              validationState === "valid"
                ? "text-green-800"
                : validationState === "invalid"
                ? "text-red-800"
                : ""
            }
          >
            {validationMessage}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default PrefixValidationInput;
